import {
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import type {
  AddonDetail,
  AddonProfile,
  AddonStats,
  TargetCluster,
} from '@internal/plugin-addon-common';

export interface AddonApi {
  list(): Promise<AddonProfile[]>;
  stats(): Promise<AddonStats>;
  get(name: string): Promise<AddonDetail>;
  targets(): Promise<TargetCluster[]>;
}

export const addonApiRef = createApiRef<AddonApi>({
  id: 'plugin.addon.service',
});

export class AddonClient implements AddonApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(options: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
  }

  /** Unwraps Backstage's error envelope so the real reason reaches the UI. */
  private async request<T>(path: string): Promise<T> {
    const base = await this.discoveryApi.getBaseUrl('addon');
    const res = await this.fetchApi.fetch(`${base}${path}`);
    if (!res.ok) {
      let detail = `${res.status} ${res.statusText}`;
      try {
        const body = await res.json();
        if (body?.error?.message) detail = body.error.message;
      } catch {
        // Non-JSON body; the status line is all we have.
      }
      throw new Error(detail);
    }
    return (await res.json()) as T;
  }

  async list() {
    return (await this.request<{ items: AddonProfile[] }>('/profiles')).items;
  }

  async stats() {
    return this.request<AddonStats>('/stats');
  }

  async get(name: string) {
    return this.request<AddonDetail>(`/profiles/${encodeURIComponent(name)}`);
  }

  async targets() {
    return (await this.request<{ items: TargetCluster[] }>('/targets')).items;
  }
}
